import type { ReactNode } from 'react'
import {
  type ColumnDef,
  type Row,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { DragOverlay, useDraggable } from '@dnd-kit/react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/tailwind.utils'
import { DocumentColumnKey, DocumentsSort, EvidenceDocument } from '../documents.types'
import { Folder } from '../collections.types'
import {
  COLUMN_TO_SORT_FIELD,
  OPTIONAL_COLUMN_DEFS,
  createActionsColumn,
  createFolderColumn,
  createTitleColumn,
} from './documents-table.columns'
import { DocumentDragHandleProvider } from './document-drag-handle-context'

type DocumentsTableProps = {
  documents: EvidenceDocument[]
  visibleColumns: DocumentColumnKey[]
  sort: DocumentsSort
  onSortChange: (sort: DocumentsSort) => void
  search: string
  onEdit: (document: EvidenceDocument) => void
  onDelete: (document: EvidenceDocument) => void
  folders: Folder[]
  activeCollectionId: string | null
  isLoading?: boolean
}

// dnd-kit measures/sizes the DragOverlay from this row's ref; the grip icon in the Title
// cell only activates the drag, via handleRef passed down through DocumentDragHandleProvider.
const DraggableDocumentRow = ({
  row,
  children,
}: {
  row: Row<EvidenceDocument>
  children: ReactNode
}) => {
  const document = row.original
  const { ref, handleRef, isDragging } = useDraggable({
    id: `draggable_document_${document.id}`,
    type: 'document',
    data: { documentId: document.id, collectionId: document.collectionId, title: document.title },
  })

  return (
    <DocumentDragHandleProvider value={{ handleRef }}>
      <TableRow ref={ref} className={cn('group', isDragging && 'opacity-40')}>
        {children}
      </TableRow>
    </DocumentDragHandleProvider>
  )
}

// Classes to pin the actions column in view during horizontal scrolling.
const STICKY_ACTIONS_CLASSES = 'sticky right-0 z-10 transition-colors'
const STICKY_ACTIONS_HOVER_BG =
  'group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,hsl(var(--background)))]'

// The clone that follows the cursor while dragging; reuses the real row's column cells
// (minus actions) so it looks like the row itself rather than a generic preview chip.
const DocumentRowPreview = ({
  documentId,
  documents,
  previewColumns,
}: {
  documentId: string
  documents: EvidenceDocument[]
  previewColumns: ColumnDef<EvidenceDocument>[]
}) => {
  const document = documents.find((doc) => doc.id === documentId)
  if (!document) return null

  return (
    <Table className="h-full w-full table-fixed border rounded-lg bg-background shadow-lg">
      <TableBody>
        <TableRow className="hover:bg-transparent">
          {previewColumns.map((column) => (
            <TableCell key={column.id}>
              {typeof column.cell === 'function'
                ? column.cell({ row: { original: document } } as never)
                : null}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  )
}

export const DocumentsTable = ({
  documents,
  visibleColumns,
  sort,
  onSortChange,
  search,
  onEdit,
  onDelete,
  folders,
  activeCollectionId,
  isLoading,
}: DocumentsTableProps) => {
  const folderNameById = new Map(folders.map((folder) => [folder.id, folder.name]))

  const columns: ColumnDef<EvidenceDocument>[] = [
    createTitleColumn(search),
    ...visibleColumns.map((key) =>
      key === 'folder' ? createFolderColumn(folderNameById) : OPTIONAL_COLUMN_DEFS[key],
    ),
    createActionsColumn(search, onEdit, onDelete, folders, activeCollectionId),
  ]
  const previewColumns = columns.slice(0, -1)

  const table = useReactTable({
    data: documents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  })

  const toggleSort = (columnId: string) => {
    const field = COLUMN_TO_SORT_FIELD[columnId]
    if (!field) return
    const nextDirection = sort.field === field && sort.direction === 'asc' ? 'desc' : 'asc'
    onSortChange({ field, direction: nextDirection })
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className="rounded-md border border-border p-8 text-center text-muted-foreground">
        No documents match your filters.
      </div>
    )
  }

  return (
    <div className="border rounded-lg overflow-hidden overflow-x-auto bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="bg-sidebar-accent">
              {hg.headers.map((header) => {
                const field = COLUMN_TO_SORT_FIELD[header.column.id]
                const isSorted = !!field && sort.field === field

                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'font-semibold',
                      header.column.id === 'actions' && [
                        STICKY_ACTIONS_CLASSES,
                        'bg-sidebar-accent',
                      ],
                    )}
                  >
                    {field ? (
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => toggleSort(header.column.id)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {isSorted && sort.direction === 'asc' && (
                          <ArrowUp className="h-3.5 w-3.5 flex-shrink-0" />
                        )}
                        {isSorted && sort.direction === 'desc' && (
                          <ArrowDown className="h-3.5 w-3.5 flex-shrink-0" />
                        )}
                        {!isSorted && (
                          <ArrowUpDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <DraggableDocumentRow key={row.id} row={row}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cn(
                    cell.column.id === 'actions' && [
                      STICKY_ACTIONS_CLASSES,
                      'bg-background',
                      STICKY_ACTIONS_HOVER_BG,
                    ],
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </DraggableDocumentRow>
          ))}
        </TableBody>
      </Table>

      <DragOverlay>
        {(source) =>
          source?.data?.documentId ? (
            <DocumentRowPreview
              documentId={String(source.data.documentId)}
              documents={documents}
              previewColumns={previewColumns}
            />
          ) : null
        }
      </DragOverlay>
    </div>
  )
}
